import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import VideoPlayer from '@kidstube/components/VideoPlayer';
import type { YouTubeVideo, YouTubeChannel } from '@kidstube/types';
import { apiFetch } from '@kidstube/lib/api';
import { decodeHtmlEntities } from '@kidstube/lib/text';

export default function WatchPage() {
  const { videoId } = useParams<{ videoId: string }>();
  const navigate = useNavigate();
  const [video, setVideo] = useState<YouTubeVideo | null>(null);
  const [channel, setChannel] = useState<YouTubeChannel | null>(null);
  const [relatedVideos, setRelatedVideos] = useState<YouTubeVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [autoplayEnabled, setAutoplayEnabled] = useState(true);
  const [preferDirectPlayback, setPreferDirectPlayback] = useState(false);
  const [playbackSettingsLoaded, setPlaybackSettingsLoaded] = useState(false);
  const [playRequestToken, setPlayRequestToken] = useState<string | null>(null);
  const relatedListRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<HTMLDivElement>(null);

  // Click-and-drag horizontal scroll for non-touch devices
  useEffect(() => {
    const el = relatedListRef.current;
    if (!el) return;

    let isDown = false;
    let startX = 0;
    let scrollStart = 0;
    let hasDragged = false;

    const onMouseDown = (e: MouseEvent) => {
      if (e.button !== 0) return;
      isDown = true;
      hasDragged = false;
      startX = e.pageX;
      scrollStart = el.scrollLeft;
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!isDown) return;
      const dx = e.pageX - startX;
      if (!hasDragged && Math.abs(dx) > 6) {
        hasDragged = true;
        el.classList.add('is-dragging');
      }
      if (!hasDragged) return;
      e.preventDefault();
      el.scrollLeft = scrollStart - dx;
    };

    const onMouseUp = () => {
      if (!isDown) return;
      isDown = false;
      el.classList.remove('is-dragging');
      if (hasDragged) {
        // Prevent the click that fires after a drag from navigating
        const preventClick = (e: Event) => { e.preventDefault(); e.stopPropagation(); };
        el.addEventListener('click', preventClick, { capture: true, once: true });
      }
    };

    el.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);

    return () => {
      el.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [relatedVideos]);

  useEffect(() => {
    const mql = window.matchMedia('(orientation: landscape) and (max-height: 500px)');
    const onOrientationChange = (e: MediaQueryListEvent | MediaQueryList) => {
      if (e.matches && playerRef.current) {
        playerRef.current.scrollIntoView({ behavior: 'instant', block: 'start' });
      }
    };
    mql.addEventListener('change', onOrientationChange);
    return () => mql.removeEventListener('change', onOrientationChange);
  }, []);

  useEffect(() => {
    apiFetch<{ preferences?: { autoplay?: boolean } }>('/api/user/preferences')
      .then((prefs) => {
        if (prefs?.preferences?.autoplay !== undefined) {
          setAutoplayEnabled(prefs.preferences.autoplay);
        }
      })
      .catch(() => {});
    apiFetch<{ settings?: { preferDirectPlayback?: boolean } }>('/api/playback-settings')
      .then((result) => {
        setPreferDirectPlayback(Boolean(result.settings?.preferDirectPlayback));
      })
      .catch(() => {})
      .finally(() => setPlaybackSettingsLoaded(true));
  }, []);

  useEffect(() => {
    async function fetchVideoData() {
      if (!videoId) return;

      setLoading(true);
      setError(null);

      try {
        const [videoData, channelsData] = await Promise.all([
          apiFetch<{ video: YouTubeVideo }>(`/api/videos/${videoId}`),
          apiFetch<{ channels?: YouTubeChannel[] }>('/api/channels'),
        ]);

        setVideo(videoData.video);

        const channelInfo = channelsData.channels?.find(c => c.id === videoData.video.channelId);
        if (channelInfo) {
          setChannel(channelInfo);
        }

        const relatedData = await apiFetch<{ videos?: YouTubeVideo[] }>(
          `/api/videos?channelId=${videoData.video.channelId}&exclude=${videoId}&limit=12`
        );
        setRelatedVideos(relatedData.videos || []);

      } catch (err) {
        setError(err instanceof Error ? err.message : 'Something went wrong');
      } finally {
        setLoading(false);
      }
    }

    fetchVideoData();
  }, [videoId]);

  useEffect(() => {
    if (!videoId) return;
    const pendingPlay = sessionStorage.getItem('kidstube.playRequest');
    if (pendingPlay === videoId) {
      setPlayRequestToken(`${videoId}:${Date.now()}`);
      sessionStorage.removeItem('kidstube.playRequest');
    } else {
      setPlayRequestToken(null);
    }
  }, [videoId]);

  const handleProgress = useCallback((_position: number, _duration: number) => {
    // Progress tracking disabled
  }, []);

  const handleVideoEnd = useCallback(() => {
    if (!autoplayEnabled || relatedVideos.length === 0) return;
    
    const nextVideo = relatedVideos[0];
    if (nextVideo) {
      navigate(`/watch/${nextVideo.id}`);
    }
  }, [autoplayEnabled, relatedVideos, navigate]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'ArrowRight') {
        relatedListRef.current?.scrollBy({ left: 250, behavior: 'smooth' });
      } else if (e.key === 'ArrowLeft') {
        relatedListRef.current?.scrollBy({ left: -250, behavior: 'smooth' });
      } else if (e.key === 'Backspace' || e.key === 'Escape') {
        window.history.back();
      } else if (e.key === 'n' || e.key === 'N') {
        if (relatedVideos.length > 0) {
          navigate(`/watch/${relatedVideos[0].id}`);
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [relatedVideos, navigate]);

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-logo">▶️</div>
        <div className="loading-text">Loading video...</div>
      </div>
    );
  }

  if (error || !video || !videoId) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon">🚫</div>
        <h2 className="empty-state-title">Video Not Available</h2>
        <p>{error || 'This video is not in your approved list.'}</p>
        <Link to="/" className="chip-btn chip-btn-primary">
          Go Back Home
        </Link>
      </div>
    );
  }

  const relatedList = relatedVideos.length > 0 && (
    <div className="watch-related">
      <h3>
        {autoplayEnabled && <span className="autoplay-indicator">Up next: </span>}
        More from {decodeHtmlEntities(video.channelTitle)}
      </h3>
      <div className="watch-related-scroll" ref={relatedListRef}>
        {relatedVideos.map((related, index) => (
          <Link
            key={related.id}
            to={`/watch/${related.id}`}
            className={`watch-related-card ${index === 0 && autoplayEnabled ? 'up-next' : ''}`}
            onClick={() => {
              sessionStorage.setItem('kidstube.playRequest', related.id);
            }}
          >
            <div className="watch-related-thumb">
              <img src={related.thumbnail} alt="" />
              {index === 0 && autoplayEnabled && (
                <span className="up-next-badge">Up Next</span>
              )}
            </div>
            <div className="watch-related-title">{decodeHtmlEntities(related.title)}</div>
          </Link>
        ))}
      </div>
    </div>
  );

  return (
    <div className="watch-layout">
      <div className="watch-main">
        <div className="watch-player" ref={playerRef}>
          {playbackSettingsLoaded ? (
            <VideoPlayer
              videoId={videoId}
              autoplay={autoplayEnabled}
              startAt={0}
              onProgress={handleProgress}
              onVideoEnd={handleVideoEnd}
              playRequestToken={playRequestToken}
              preferDirectPlayback={preferDirectPlayback}
            />
          ) : (
            <div className="player-wrapper">
              <div className="player-container" />
            </div>
          )}
        </div>

        <div className="watch-info">
          <h1 className="watch-title">{decodeHtmlEntities(video.title)}</h1>

          <div className="watch-channel-row">
            <div className="watch-channel-info">
              {channel?.thumbnail ? (
                <img
                  src={channel.thumbnail}
                  alt={decodeHtmlEntities(video.channelTitle)}
                  className="watch-channel-avatar"
                />
              ) : (
                <div className="watch-channel-avatar watch-channel-avatar-placeholder">
                  {decodeHtmlEntities(video.channelTitle)[0]}
                </div>
              )}
              <div className="watch-channel-text">
                <span className="watch-channel-name">{decodeHtmlEntities(video.channelTitle)}</span>
              </div>
            </div>

            <div className="watch-controls">
              <button
                className={`chip-btn ${autoplayEnabled ? 'chip-btn-primary' : 'chip-btn-ghost'}`}
                onClick={() => {
                  const newValue = !autoplayEnabled;
                  setAutoplayEnabled(newValue);
                  apiFetch('/api/user/preferences', {
                    method: 'POST',
                    json: { autoplay: newValue },
                  }).catch(() => {});
                }}
              >
                {autoplayEnabled ? '▶ Autoplay On' : '⏸ Autoplay Off'}
              </button>
              <Link to="/" className="chip-btn chip-btn-ghost">
                ← Back
              </Link>
            </div>
          </div>

          {relatedList}
        </div>
      </div>
    </div>
  );
}
